import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

const VEHICLE_OPTIONS = [
  { value: 'road_diesel', label: 'Road — Diesel HCV', factor: 0.11, unit: 'tonne_km', baseline: true },
  { value: 'road_cng',    label: 'Road — CNG vehicle',  factor: 0.07, unit: 'tonne_km' },
  { value: 'road_ev',     label: 'Road — EV truck',     factor: 0.012, unit: 'tonne_km' },
  { value: 'rail_electric', label: 'Rail — Electric',   factor: 0.028, unit: 'tonne_km' },
  { value: 'sea_container', label: 'Sea — Container',   factor: 0.016, unit: 'tonne_km' },
  { value: 'air_cargo',   label: 'Air — Cargo',         factor: 0.602, unit: 'tonne_km' },
];

const ALTERNATIVES = [
  { from: 'Diesel HCV', to: 'CNG vehicle', reduction: 25, cost_delta: '+5% OpEx', roi: '18 months', link: '/vendors?category=logistics' },
  { from: 'Diesel HCV', to: 'Electric truck (EV)', reduction: 70, cost_delta: '+20% upfront', roi: '36 months', link: '/vendors?category=logistics' },
  { from: 'Road freight', to: 'Rail freight (electric)', reduction: 75, cost_delta: 'Similar cost', roi: 'Immediate', link: '/vendors?category=logistics' },
  { from: 'Air freight', to: 'Sea freight', reduction: 85, cost_delta: '-20% cost', roi: 'Immediate', link: '/vendors?category=logistics' },
];

export default function GreenLogistics() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ activity_type: 'road_diesel', distance_km: '', weight_tonnes: '', notes: '', period: '2025-Q4' });

  const selectedVehicle = VEHICLE_OPTIONS.find((v) => v.value === form.activity_type);
  const baselineFactor = 0.11; // diesel HCV
  const tco2eEstimate = form.distance_km && form.weight_tonnes
    ? Math.round((parseFloat(form.distance_km) * parseFloat(form.weight_tonnes)) * (selectedVehicle?.factor || 0) / 10) / 100
    : null;
  const co2eSaved = tco2eEstimate !== null && selectedVehicle && !selectedVehicle.baseline
    ? Math.round((parseFloat(form.distance_km) * parseFloat(form.weight_tonnes)) * (baselineFactor - selectedVehicle.factor) / 10) / 100
    : null;

  const load = () => {
    api.get('/services/logs?module=logistics').then((r) => setLogs(r.data.logs || [])).catch(() => {});
    api.get('/services/summary').then((r) => setSummary(r.data.summary?.logistics)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.distance_km || !form.weight_tonnes) { setError('Distance and weight are required.'); return; }
    setSubmitting(true);
    try {
      await api.post('/services/logs', {
        module: 'logistics', activity_type: form.activity_type,
        quantity: parseFloat(form.distance_km) * parseFloat(form.weight_tonnes),
        unit: 'tonne_km', distance_km: parseFloat(form.distance_km),
        weight_tonnes: parseFloat(form.weight_tonnes), notes: form.notes, period: form.period,
      });
      setSuccess('Logistics trip logged successfully.');
      setForm((f) => ({ ...f, distance_km: '', weight_tonnes: '', notes: '' }));
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to log trip.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="page-shell page-stack">
      <section className="section-stack">
        <div className="section-tag">Green Services — Logistics</div>
        <h1>Low-Carbon Logistics</h1>
        <p className="section-copy">Track and reduce emissions from freight, distribution, and company vehicles. Switch to lower-carbon transport alternatives.</p>
      </section>

      {/* Stats */}
      <section className="stats-grid">
        <div className="surface-panel stat-card">
          <div className="stat-label">Trips logged</div>
          <div className="stat-value" style={{ color: 'var(--neon-green)' }}>{summary?.log_count || 0}</div>
          <div className="stat-sub">total logistics activities</div>
        </div>
        <div className="surface-panel stat-card">
          <div className="stat-label">CO2e emitted</div>
          <div className="stat-value" style={{ color: 'var(--neon-orange)' }}>
            {((summary?.total_co2e_emitted_kg || 0) / 1000).toFixed(1)} <span style={{ fontSize: '1rem' }}>tCO2e</span>
          </div>
          <div className="stat-sub">from logged logistics activities</div>
        </div>
        <div className="surface-panel stat-card">
          <div className="stat-label">CO2e saved</div>
          <div className="stat-value" style={{ color: 'var(--neon-cyan)' }}>
            {((summary?.total_co2e_saved_kg || 0) / 1000).toFixed(1)} <span style={{ fontSize: '1rem' }}>tCO2e</span>
          </div>
          <div className="stat-sub">vs. diesel baseline</div>
        </div>
        <Link to="/vendors?category=logistics" style={{ textDecoration: 'none' }}>
          <div className="surface-panel stat-card">
            <div className="stat-label">Green vendors</div>
            <div className="stat-value" style={{ color: 'var(--neon-yellow)' }}>3</div>
            <div className="stat-sub">verified logistics providers</div>
          </div>
        </Link>
      </section>

      {/* How it works */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '1rem' }}>How it works</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
          {[
            { step: '01', title: 'Log trips', desc: 'Enter distance, weight, and vehicle type for each logistics activity.' },
            { step: '02', title: 'Get alternatives', desc: 'See CO2e reduction potential for switching to CNG, EV, or rail alternatives.' },
            { step: '03', title: 'Track savings', desc: 'Monitor cumulative CO2e saved vs. diesel baseline over time.' },
          ].map((s) => (
            <div key={s.step} style={{ padding: '1.25rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid var(--border)' }}>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--neon-cyan)', marginBottom: '0.5rem' }}>{s.step}</div>
              <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.4rem' }}>{s.title}</div>
              <div className="stat-sub">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Log Form */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '1rem' }}>Log a logistics trip</div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label className="form-label">Vehicle / mode *</label>
              <select className="form-input" value={form.activity_type} onChange={(e) => setForm((f) => ({ ...f, activity_type: e.target.value }))}>
                {VEHICLE_OPTIONS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Distance (km) *</label>
              <input className="form-input" type="number" min="0" step="any" placeholder="e.g. 500" value={form.distance_km} onChange={(e) => setForm((f) => ({ ...f, distance_km: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Cargo weight (tonnes) *</label>
              <input className="form-input" type="number" min="0" step="any" placeholder="e.g. 10" value={form.weight_tonnes} onChange={(e) => setForm((f) => ({ ...f, weight_tonnes: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Period</label>
              <select className="form-input" value={form.period} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))}>
                {['2025-Q3', '2025-Q4', '2026-Q1', '2026-Q2'].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {tco2eEstimate !== null && (
            <div style={{ padding: '0.75rem 1rem', background: 'rgba(255,100,50,0.07)', borderRadius: '8px', border: '1px solid rgba(255,100,50,0.2)', marginBottom: '1rem', display: 'flex', gap: '2rem' }}>
              <div>
                <div className="stat-label">Estimated CO2e</div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1rem', color: 'var(--neon-orange)' }}>{tco2eEstimate} tCO2e</div>
              </div>
              {co2eSaved !== null && co2eSaved > 0 && (
                <div>
                  <div className="stat-label">vs. diesel baseline saved</div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1rem', color: 'var(--neon-green)' }}>-{co2eSaved} tCO2e</div>
                </div>
              )}
              <div>
                <div className="stat-label">Emission factor</div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.72rem', color: 'var(--muted2)', marginTop: '0.3rem' }}>
                  {selectedVehicle?.factor} kg CO2e/tonne-km
                </div>
              </div>
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <label className="form-label">Notes</label>
            <input className="form-input" type="text" placeholder="e.g. Inbound shipment from supplier A" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>

          {error && <div style={{ color: 'var(--neon-pink)', fontFamily: 'Space Mono', fontSize: '0.72rem', marginBottom: '0.5rem' }}>{error}</div>}
          {success && <div style={{ color: 'var(--neon-green)', fontFamily: 'Space Mono', fontSize: '0.72rem', marginBottom: '0.5rem' }}>{success}</div>}
          <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Logging…' : 'Log trip'}</button>
        </form>
      </section>

      {/* Decarbonization alternatives */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-header-row" style={{ marginBottom: '1.25rem' }}>
          <div className="section-tag">Fleet decarbonization options</div>
          <Link to="/vendors?category=logistics" className="btn btn-secondary btn-sm">Find green vendors →</Link>
        </div>
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {ALTERNATIVES.map((a, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto auto', gap: '1rem', alignItems: 'center', padding: '1rem 1.25rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid var(--border)' }}>
              <div>
                <div className="stat-label">Switch from</div>
                <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '0.85rem', color: 'var(--text)' }}>{a.from}</div>
              </div>
              <div>
                <div className="stat-label">Switch to</div>
                <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '0.85rem', color: 'var(--neon-green)' }}>→ {a.to}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div className="stat-label">CO2e reduction</div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.9rem', color: 'var(--neon-green)' }}>-{a.reduction}%</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div className="stat-label">Cost delta</div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.75rem', color: 'var(--muted2)' }}>{a.cost_delta}</div>
              </div>
              <Link to={a.link} className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }}>Find vendor</Link>
            </div>
          ))}
        </div>
      </section>

      {/* Activity log */}
      {logs.length > 0 && (
        <section className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-header-row" style={{ marginBottom: '1rem' }}>
            <div className="section-tag">Activity log</div>
          </div>
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Vehicle</th><th>Distance</th><th>Weight</th><th>CO2e emitted</th><th>CO2e saved</th></tr>
            </thead>
            <tbody>
              {logs.slice(0, 10).map((l) => (
                <tr key={l.id}>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--muted2)' }}>{new Date(l.created_at).toLocaleDateString('en-IN')}</td>
                  <td style={{ fontSize: '0.82rem' }}>{VEHICLE_OPTIONS.find((v) => v.value === l.activity_type)?.label || l.activity_type}</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.75rem' }}>{l.distance_km || '—'} km</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.75rem' }}>{l.weight_tonnes || '—'} t</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', color: 'var(--neon-orange)' }}>{(l.co2e_emitted_kg / 1000).toFixed(3)} tCO2e</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', color: 'var(--neon-green)' }}>-{(l.co2e_saved_kg / 1000).toFixed(3)} tCO2e</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
